import React, { useCallback, useMemo } from 'react';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { NewModal } from 'Views/Components/Modal/NewModal';
import PurchaseReviewBody, { PurchaseReviewLine } from 'Views/Components/_HumanWritten/Payment/PurchaseReviewBody';
import { GiftCertificateUsageEntity } from 'Models/Entities';
import { useProductList } from 'Modules/Products/Hooks/useProductList';
import { useAnytimeTicketStore } from '../State/AnytimeTicketContext';

interface FooterProps {
	onCancel: () => void;
	onDone: () => void;
	scrollModalToTop: () => void;
}

/**
 * Review step for an anytime-ticket purchase (#2613) — where a credit voucher is applied, before the
 * payment step mounts. See {@link PurchaseReviewBody} for why it cannot go on the payment step.
 */
export default function AnytimeTicketReviewStep() {
	const transactionId = useAnytimeTicketStore(s => s.transactionId);
	const selectedProductId = useAnytimeTicketStore(s => s.selectedProductId);
	const productQuantity = useAnytimeTicketStore(s => s.productQuantity);
	const selectedCustomer = useAnytimeTicketStore(s => s.selectedCustomer);
	const setGiftCertUsages = useAnytimeTicketStore(s => s.setGiftCertUsages);

	const { data } = useProductList();
	const product = data?.items.find(p => p.id === selectedProductId);

	const detailLines = useMemo<PurchaseReviewLine[]>(() => {
		const lines: PurchaseReviewLine[] = [];
		if (product?.name) {
			lines.push({ label: 'Product', value: product.name });
		}
		if (productQuantity > 1) {
			lines.push({ label: 'Quantity', value: `${productQuantity}` });
		}
		if (selectedCustomer) {
			const name = `${selectedCustomer.firstName} ${selectedCustomer.lastName}`.trim();
			if (name) {
				lines.push({ label: 'Customer', value: name });
			}
		}
		return lines;
	}, [product?.name, productQuantity, selectedCustomer]);

	const onUsagesChange = useCallback(
		(usages: GiftCertificateUsageEntity[]) => setGiftCertUsages(usages),
		[setGiftCertUsages],
	);

	return (
		<PurchaseReviewBody
			transactionId={transactionId}
			detailLines={detailLines}
			onUsagesChange={onUsagesChange}
		/>
	);
}

AnytimeTicketReviewStep.Footer = function AnytimeTicketReviewFooter({ scrollModalToTop }: FooterProps) {
	const abortPurchase = useAnytimeTicketStore(s => s.abortPurchase);
	const goToPayment = useAnytimeTicketStore(s => s.goToPayment);

	return (
		<NewModal.Footer>
			<Button display={Display.Outline} colors={Colors.Grey} newStyles onClick={abortPurchase}>
				Back
			</Button>
			<Button
				display={Display.Solid}
				colors={Colors.Primary}
				newStyles
				onClick={() => {
					scrollModalToTop();
					goToPayment();
				}}
				buttonProps={{ 'data-test-id': 'anytime-ticket-continue-to-payment' } as any}
			>
				Continue to payment
			</Button>
		</NewModal.Footer>
	);
};
